import * as React from 'react';
import { Plus } from 'lucide-react';
import { DashboardTableFilterTrigger, DashboardTableSearchInput } from '@shigo-ui/dashboard-ui';
import {
  AutocompleteFilter,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import {
  campaignStatusFilterOptions,
  resolveCampaignStatusLabel,
  type CampaignStatus,
} from '../../model';
import { CampaignBrandSelect, type CampaignBrandFilter } from './CampaignBrandSelect';
import { campaignStatusFilterLabel, toggleCampaignStatus } from './campaignStatusFilter';

type CampaignsTableToolbarProps = {
  showAgencyFilter: boolean;
  agencyIds: string[];
  onAgenciesChange: (agencyIds: string[]) => void;
  /** Omitted when the table is embedded in a brand's details page. */
  brandFilter: CampaignBrandFilter | undefined;
  search: string;
  onSearchChange: (value: string) => void;
  statuses: readonly CampaignStatus[];
  onStatusesChange: (statuses: CampaignStatus[]) => void;
  onCreate: (() => void) | undefined;
};

export function CampaignsTableToolbar({
  showAgencyFilter,
  agencyIds,
  onAgenciesChange,
  brandFilter,
  search,
  onSearchChange,
  statuses,
  onStatusesChange,
  onCreate,
}: CampaignsTableToolbarProps) {
  const selectedStatuses = new Set(statuses);
  return (
    <>
      {showAgencyFilter ? (
        <AutocompleteFilter
          label={dashboardT('agencyAssignment.filter')}
          options={[]}
          selectedIds={agencyIds}
          onSelectedIdsChange={onAgenciesChange}
          selectAll={false}
          emptyMessage={dashboardT('agencyWorkspace.unavailable')}
          className="hidden"
        />
      ) : null}
      {brandFilter ? <CampaignBrandSelect {...brandFilter} /> : null}
      <CampaignsSearchInput key={search} initialValue={search} onChange={onSearchChange} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <DashboardTableFilterTrigger
            label={campaignStatusFilterLabel(statuses)}
            aria-label={dashboardT('campaigns.statusFilter')}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {campaignStatusFilterOptions.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={selectedStatuses.has(status)}
              onSelect={(event) => {
                event.preventDefault();
              }}
              onCheckedChange={(checked) => {
                onStatusesChange(toggleCampaignStatus(statuses, status, checked));
              }}
            >
              {resolveCampaignStatusLabel(status)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {onCreate ? (
        <Button onClick={onCreate}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {dashboardT('campaigns.create')}
        </Button>
      ) : null}
    </>
  );
}

function CampaignsSearchInput({
  initialValue,
  onChange,
}: {
  initialValue: string;
  onChange: (value: string) => void;
}) {
  // Emits only typed changes, so mounting (or leaving while a route loads) never navigates.
  const search = useDebouncedValue(initialValue, onChange, 650);
  return (
    <DashboardTableSearchInput
      value={search.draft}
      aria-label={dashboardT('campaigns.search')}
      placeholder={dashboardT('campaigns.search')}
      onChange={(event) => {
        search.setDraft(event.target.value);
      }}
    />
  );
}
